import { Injectable, Injector, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../../environment/environment";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { ApiClient } from "../../api/api";
import { ToastrService } from "ngx-toastr";
import { catchError, tap } from "rxjs/operators";
import { User } from "../../../company/models/users.model";
import { Permission, Role } from "../../../company/models/role.model";
import { Router } from "@angular/router";
import { PullToRefreshService } from "../pull-to-refresh.service";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private userSubject: BehaviorSubject<any> = new BehaviorSubject(null);
  private userSignal = signal<any>(null);
  private API_URL = environment.API_URL;
  authenticatedUser = signal<any>(null);
  private currentUser!: User;
  private toastr?: ToastrService;

  constructor(
    private http: HttpClient,
    private apiClient: ApiClient,
    private router: Router,
    private injector: Injector,
    private pullToRefreshService: PullToRefreshService
  ) {
    try {
      this.toastr = this.injector.get(ToastrService);
    } catch {
      // Toastr not configured in tests – ignore
      this.toastr = undefined;
    }

    // Register refresh callbacks for user data
    this.pullToRefreshService.registerRefreshCallback('user-profile', () => this.refreshUserProfileCache());
  }

  // AJOUTEZ CETTE MÉTHODE
  isLoggedIn(): boolean {
    const token = localStorage.getItem("jwtToken");
    return !!token; // Retourne true si le token existe, false sinon
  }

  login(login: string, password: string): Observable<any> {
    const body = { login, password };

    return this.http.post<any>(`${this.API_URL}/api/auth/login`, body).pipe(
      tap((response) => {
        if (
          response.status === "SUCCEED" &&
          response.data &&
          response.data.jwtToken
        ) {
          const jwtToken = response.data.jwtToken;
          const message = response.message;
          const plateform = response.data.platform;
          localStorage.setItem("jwtToken", jwtToken);
          this.toastr?.success(`${message}`, "Success");
        } else {
          throw new Error("Login failed or JWT token not found");
        }
      }),
      catchError((error: any) => {
        const errorMessage =
          error.error?.message || "Une erreur est survenue lors de la connexion";
        const errorStatus = error.error?.status || "Erreur";
        this.toastr?.error(errorMessage, errorStatus);
        return throwError(error);
      })
    );
  }

  logout() {
    localStorage.removeItem("jwtToken");
    this.userSignal.set(null);
    this.clearUserCache();
  }

  loadUser(): Observable<any> {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      if (!this.userSubject.value) {
        return this.apiClient.getAll<any>("api/auth/account/me").pipe(
          tap((user) => {
            this.currentUser = user.data;
            this.userSignal.set(user.data);
            this.userSubject.next(user);
          }),
          catchError((error) => {
            this.toastr?.error(error.error?.message);
            if (error.error?.status === "401") {
              this.logout();
              this.router.navigate(["/login"]);
            }
            return throwError(error);
          })
        );
      }
    }
    return this.userSubject.asObservable();
  }

  clearUserCache() {
    this.userSubject.next(null);
  }

  getUser(): Observable<any> {
    return this.userSubject.asObservable();
  }

  getUserRole(): Role | null {
    return this.currentUser ? this.currentUser.role : null;
  }

  getUserPermissions(): Permission[] {
    return this.currentUser?.role?.permissions || [];
  }

  hasPermission(code: string): boolean {
    if (this.isAdmin("COMPANY_ADMIN") || this.isAdmin("BO_ADMIN")) {
      return true;
    } else {
      return this.getUserPermissions().some(
        (perm) => perm?.code === code && perm?.state === "ACTIVE"
      );
    }
  }

  hasRole(roleCode: string): boolean {
    const userRole = this.getUserRole();
    return userRole?.code === roleCode;
  }

  isAdmin(code: string): boolean {
    return this.hasRole(code);
  }

  setAuthenticatedUser(user: any) {
    this.authenticatedUser.set(user);
  }

  get user() {
    return this.userSignal();
  }

  /**
   * Refresh user profile cache
   */
  private async refreshUserProfileCache(): Promise<void> {
    // Clear user cache to force reload
    this.clearUserCache();
    this.pullToRefreshService.clearCache('user-profile');
  }
}
